import { useCallback, useEffect, useRef, useState } from 'react';
import { useAbility } from '@casl/react';

import RecentSearchesManager from '../storage/RecentSearchesManager';
import { AbilityContext } from '../../../abilities/Can';
import { addSearchResultToFilterBar, isReactAssetOverviewActive } from '../helpers/searchUtils';
import { emitSearchAssetsEvent } from '../helpers/emitSearchAssetsEvent';

export function useRecentSearches() {
	const [searchTerms, setSearchTerms] = useState<string[]>([]);
	const refManager = useRef<RecentSearchesManager>();
	const recentSearchesManager = refManager.current;

	const ability = useAbility(AbilityContext);
	const isStickySearchEnabled = ability.can('show', 'sticky_search');

	useEffect(() => {
		refManager.current = new RecentSearchesManager();
	}, []);

	const updateSearchTerms = useCallback(async () => {
		if (!recentSearchesManager) {
			return;
		}

		const recentSearches = await recentSearchesManager.getAll();
		setSearchTerms(recentSearches || []);
	}, [recentSearchesManager]);

	useEffect(() => {
		if (!recentSearchesManager) {
			return;
		}

		updateSearchTerms();
	}, [recentSearchesManager, updateSearchTerms]);

	const executeSearch = (term: string) => {
		recentSearchesManager?.add(term);

		const params = {
			field: isStickySearchEnabled ? 'singletext' : 'text',
			value: term,
			filterType: 'add',
		};

		const reactAssetOverviewIsActive = isReactAssetOverviewActive();

		// In the "Collections" view, we are not in ReactOverview, so we could not react to the custom search event
		if (reactAssetOverviewIsActive) {
			emitSearchAssetsEvent(params);
			addSearchResultToFilterBar(params.field, params.value);

			return;
		}

		const urlSearchParams = new URLSearchParams({
			...params,
			filterKey: 'savedFilters',
		}).toString();

		const setFiltersRequestUrl = `/search/set/?${urlSearchParams}`;
		window.location.assign(setFiltersRequestUrl);
	};

	const onNewRecentSearch = (term: string) => {
		recentSearchesManager?.add(term);
		updateSearchTerms();
	};

	return { executeSearch, onNewRecentSearch, searchTerms };
}
